import React, { useRef, useEffect, useCallback } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Paper,
  TableHead,
  TableRow,
  TableSortLabel,
  Collapse,
  IconButton,
  Box,
  Typography,
  LinearProgress,
} from 'components/mui';
import Skeleton from '@mui/material/Skeleton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { SeverityChip } from 'components/shared/SeverityChip';
import { EmptyState } from 'components/shared/EmptyState';
import { getTimeAgo } from 'components/shared/utils';
import { incidentsHeadTableCells } from './incidentsHeadTableCells';
import type { Incident, IncidentStatus } from '../../types/canvision.types';
import IncidentStatusBadge from '../shared/IncidentStatusBadge';

import IncidentAccordionRow from './IncidentAccordionRow';

// ---------------------------------------------------------------------------
// Column definitions (imported from shared incidentsHeadTableCells)
// ---------------------------------------------------------------------------

const HEAD_CELLS = incidentsHeadTableCells;
const TOTAL_COLS = HEAD_CELLS.length;
const SKELETON_ROW_COUNT = 6;

// ---------------------------------------------------------------------------
// Memoized row component — prevents re-renders when sibling rows change
// ---------------------------------------------------------------------------

interface IncidentRowProps {
  incident: Incident;
  isOpen: boolean;
  onExpandToggle: (id: number) => void;
  onViewIncident?: (incident: Incident) => void;
  onStatusChange?: (incident: Incident, status: IncidentStatus) => void;
}

const IncidentRow = React.memo(function IncidentRow({
  incident,
  isOpen,
  onExpandToggle,
  onViewIncident,
  onStatusChange,
}: IncidentRowProps) {
  return (
    <React.Fragment>
      {/* Data row */}
      <TableRow
        hover
        onClick={() => onExpandToggle(incident.id)}
        sx={{ cursor: 'pointer' }}
      >
        {/* Expand/collapse toggle */}
        <TableCell>
          <IconButton
            aria-label={isOpen ? 'Collapse' : 'Expand'}
            aria-expanded={isOpen}
            size="small"
          >
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        {/* Title + camera subtitle */}
        <TableCell>
          <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
            {incident.title}
          </Typography>
          {(incident.camera_display_name || incident.camera_name) && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {incident.camera_display_name || incident.camera_name}
            </Typography>
          )}
        </TableCell>

        {/* Severity chip */}
        <TableCell>
          <SeverityChip severity={incident.severity} size="small" />
        </TableCell>

        {/* Status chip */}
        <TableCell>
          <IncidentStatusBadge status={incident.status} size="small" />
        </TableCell>

        {/* Created -- relative time */}
        <TableCell>
          <Typography variant="body2" noWrap>
            {getTimeAgo(incident.created_at)}
          </Typography>
        </TableCell>
      </TableRow>

      {/* Collapsible accordion row */}
      <TableRow>
        <TableCell
          sx={{ p: 0, overflowX: 'hidden', maxWidth: '100%' }}
          colSpan={TOTAL_COLS}
        >
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              <IncidentAccordionRow
                incident={incident}
                onView={onViewIncident}
                onStatusChange={onStatusChange}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
});

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export type SortDir = 'asc' | 'desc';

export interface IncidentsTableProps {
  /** The incident rows to render */
  incidents: Incident[];
  /** Whether the data is currently being fetched */
  loading: boolean;
  /** Whether more data is available beyond what is currently loaded */
  hasMore: boolean;
  /** Callback when the user scrolls near the bottom and more data should load */
  onLoadMore: () => void;
  /** The id of the currently expanded row (null / undefined = none) */
  expandedId: number | null;
  /** Toggle expand/collapse for a given incident id */
  onExpandToggle: (id: number) => void;
  /** Current sort column property */
  sortBy: string;
  /** Current sort direction */
  sortDir: SortDir;
  /** Callback when the user clicks a sortable column header */
  onSortChange: (property: string) => void;
  /** Total count of incidents (may differ from incidents.length if paged) */
  totalCount?: number;
  /** Called when the user clicks "View" on an incident row */
  onViewIncident?: (incident: Incident) => void;
  /** Called when the user clicks a status-changing action button */
  onStatusChange?: (incident: Incident, status: IncidentStatus) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function IncidentsTable({
  incidents,
  loading,
  hasMore,
  onLoadMore,
  expandedId,
  onExpandToggle,
  sortBy,
  sortDir,
  onSortChange,
  totalCount,
  onViewIncident,
  onStatusChange,
}: IncidentsTableProps) {
  const tableRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // ---------- Infinite scroll via IntersectionObserver ----------

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loading) {
        onLoadMore();
      }
    },
    [hasMore, loading, onLoadMore],
  );

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleIntersect, {
      root: null,
      rootMargin: '200px',
      threshold: 0,
    });

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleIntersect]);

  // ---------- Helpers ----------

  const displayedTotal = totalCount ?? incidents.length;

  // ---------- Skeleton rows for initial load ----------

  if (loading && incidents.length === 0) {
    return (
      <Paper ref={tableRef} elevation={1}>
        <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Skeleton variant="text" width={180} />
        </Box>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                {HEAD_CELLS.map((cell) => (
                  <TableCell key={cell.id}>
                    {cell.label && <Skeleton variant="text" width={60} />}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {Array.from({ length: SKELETON_ROW_COUNT }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Skeleton variant="circular" width={28} height={28} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="80%" />
                    <Skeleton variant="text" width="50%" sx={{ mt: 0.5 }} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" width={64} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="rounded" width={72} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width={90} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  }

  // ---------- Empty state ----------

  if (!loading && incidents.length === 0) {
    return (
      <Paper elevation={1}>
        <EmptyState
          icon={<ReportProblemOutlinedIcon sx={{ fontSize: 48, color: 'text.secondary' }} />}
          title="No incidents"
          message="There are no incidents matching the current filters. Incidents appear here when events are promoted or created."
          size="medium"
        />
      </Paper>
    );
  }

  // ---------- Main table ----------

  return (
    <Paper ref={tableRef} elevation={1}>
      {/* "Showing X of Y incidents" header — Customers pattern */}
      <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" color="text.secondary">
          Showing {incidents.length} of {displayedTotal} incidents
        </Typography>
      </Box>

      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 560 }}>
          {/* ---------- Head ---------- */}
          <TableHead>
            <TableRow>
              {HEAD_CELLS.map((cell) => {
                const isActive = sortBy === cell.property;
                const direction = isActive ? sortDir : 'asc';

                return (
                  <TableCell
                    key={cell.id}
                    sortDirection={isActive ? sortDir : false}
                  >
                    {cell.property ? (
                      <TableSortLabel
                        active={isActive}
                        direction={direction}
                        onClick={() => onSortChange(cell.property)}
                      >
                        {cell.label}
                      </TableSortLabel>
                    ) : null}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>

          {/* ---------- Body ---------- */}
          <TableBody>
            {incidents.map((incident) => (
              <IncidentRow
                key={incident.id}
                incident={incident}
                isOpen={expandedId === incident.id}
                onExpandToggle={onExpandToggle}
                onViewIncident={onViewIncident}
                onStatusChange={onStatusChange}
              />
            ))}

            {/* Loading indicator when fetching more rows */}
            {loading && incidents.length > 0 && (
              <TableRow>
                <TableCell colSpan={TOTAL_COLS}>
                  <Box sx={{ width: '100%', py: 1 }}>
                    <LinearProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* End of list */}
      {!loading && !hasMore && incidents.length > 0 && (
        <Box sx={{ p: 1, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            End of list
          </Typography>
        </Box>
      )}

      {/* Sentinel element for IntersectionObserver infinite scroll */}
      <div ref={sentinelRef} style={{ height: 1 }} />
    </Paper>
  );
}

export default IncidentsTable;
